<template>
  <div class="news-editor-container">
    <a-spin :spinning="loading">
      <a-page-header
        :title="isEdit ? '编辑资讯' : '新建资讯'"
        @back="handleBack"
      >
        <template slot="extra">
          <a-button @click="handleBack">取消</a-button>
          <CommonPermissions :p="isEdit ? $permissions.NEWS.UPDATE : $permissions.NEWS.CREATE">
            <a-button type="primary" :loading="submitting" @click="handleSubmit">
              {{ isEdit ? '保存修改' : '创建资讯' }}
            </a-button>
          </CommonPermissions>
        </template>
      </a-page-header>

      <a-card :bordered="false" style="margin-top: 16px;">
        <a-form-model
          ref="formRef"
          :model="form"
          :rules="rules"
          :label-col="{ span: 3 }"
          :wrapper-col="{ span: 18 }"
        >
          <a-form-model-item label="标题" prop="title">
            <a-input
              v-model="form.title"
              placeholder="请输入资讯标题"
              :maxLength="100"
              allowClear
            />
          </a-form-model-item>

          <a-form-model-item label="分类" prop="categoryId">
            <a-select
              v-model="form.categoryId"
              placeholder="请选择资讯分类"
              style="width: 200px;"
            >
              <a-select-option :value="1">通知</a-select-option>
              <a-select-option :value="2">动态</a-select-option>
              <a-select-option :value="3">政策</a-select-option>
            </a-select>
          </a-form-model-item>

          <a-form-model-item label="摘要" prop="summary">
            <a-textarea
              v-model="form.summary"
              placeholder="请输入资讯摘要，不填则自动从内容截取"
              :rows="3"
              :maxLength="500"
              allowClear
            />
          </a-form-model-item>

          <a-form-model-item label="封面图片" prop="coverImageUrl">
            <common-upload
              path="news/cover/"
              :defaultUrl="form.coverUrl"
              @changeImgUri="handleCoverChange"
            />
            <!-- <div class="upload-tip">建议尺寸：800x600，支持 JPG、PNG 格式，大小不超过 2MB</div> -->
          </a-form-model-item>

          <a-form-model-item label="内容" prop="content" class="editor-item">
            <a-spin :spinning="editorLoading">
              <editor
                v-model="form.content"
                :init="editorInit"
              />
            </a-spin>
          </a-form-model-item>
        </a-form-model>
      </a-card>
    </a-spin>
  </div>
</template>

<script>
const OSS = require('ali-oss')
import tinymce from "tinymce/tinymce"
import Editor from "@tinymce/tinymce-vue"
import "tinymce/themes/silver/theme"

// tinymce插件
import "tinymce/plugins/link"
import "tinymce/icons/default/icons.min.js"
import "tinymce/plugins/fullscreen"
import "tinymce/plugins/image"
import "tinymce/plugins/media"
import "tinymce/plugins/code"
import "tinymce/plugins/table"
import "tinymce/plugins/lists"
import "tinymce/plugins/paste"
import "tinymce/plugins/preview"

import NewsManageApi from '@/api/src/news/api/NewsManageApi'
import OssApi from '@/api/src/oss/api/OssApi'
import CommonUpload from '@/components/CommonUpload.vue'
import CommonPermissions from '@/components/CommonPermissions'
import { uploadImageMultipart, validateJpgPngMax2M } from '@/utils/ossMultipartImageUpload'

const newsManageApi = new NewsManageApi()
const ossApi = new OssApi()

export default {
  name: 'NewsEditor',
  components: {
    Editor,
    CommonUpload,
    CommonPermissions,
  },
  data() {
    return {
      loading: false,
      submitting: false,
      editorLoading: false,
      isEdit: false,
      newsId: null,
      form: {
        title: '',
        categoryId: undefined,
        content: '',
        summary: '',
        coverUrl: '',
        coverUri: ''
      },
      rules: {
        title: [
          { required: true, message: '请输入资讯标题', trigger: 'blur' },
          { max: 100, message: '标题不能超过100个字符', trigger: 'blur' }
        ],
        categoryId: [
          { required: true, message: '请选择资讯分类', trigger: 'change' }
        ],
        content: [
          { required: true, message: '请输入资讯内容', trigger: 'blur' }
        ]
      },
      editorInit: {
        skin_url: "/tinymce/skins/ui/oxide",
        // language_url: "/tinymce/langs/zh_CN.js",
        // language: "zh_CN",
        plugins: ["link", "fullscreen", "image", "media", "code", "table", "lists", "paste", "preview"],
        file_picker_types: 'image media',
        file_picker_callback: (callback, value, meta) => this.handleFilePicker(callback, value, meta),
        automatic_uploads: false,
        toolbar: [
          "undo redo | bold italic underline strikethrough | fontsizeselect formatselect",
          "alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | image media link unlink | table | preview fullscreen code"
        ],
        height: 500,
        branding: false,
        menubar: 'file edit view insert format tools table',
        paste_data_images: true,
        content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; font-size: 14px; line-height: 1.6; }',
        images_upload_handler: (blobInfo, success, failure) => this.handleImageUpload(blobInfo, success, failure)
      }
    }
  },
  created() {
    const { id } = this.$route.query
    if (id) {
      this.isEdit = true
      this.newsId = id
      this.fetchNewsDetail()
    }
  },
  methods: {
    // 获取资讯详情
    async fetchNewsDetail() {
      this.loading = true
      try {
        const resp = await newsManageApi.getNewsDetailForAdmin(this.newsId)
        console.log('资讯详情:', resp)
        
        this.form = {
          title: resp.title || '',
          categoryId: resp.categoryId,
          content: resp.content || '',
          summary: resp.summary || '',
          coverUrl: resp.coverUrl || '',
          coverUri: resp.coverUri || '',
        }
      } catch (error) {
        console.error('获取资讯详情失败:', error)
        this.$message.error('获取资讯详情失败')
      } finally {
        this.loading = false
      }
    },

    // 封面图片变更
    handleCoverChange(uri) {
      console.log('封面图片变更:', uri)
      this.form.coverUri = uri
      if (uri) {
        // 构建完整URL用于预览
        // URI会在提交时使用
      } else {
        this.form.coverUrl = ''
      }
    },

    // 文件选择器回调
    handleFilePicker(callback, value, meta) {
      const input = document.createElement('input')
      input.setAttribute('type', 'file')
      
      if (meta.filetype === 'media') {
        input.setAttribute('accept', 'video/*,audio/*')
      } else if (meta.filetype === 'image') {
        input.setAttribute('accept', 'image/*')
      } else {
        input.setAttribute('accept', '*')
      }

      input.oninput = async (e) => {
        const files = e.target.files
        if (files && files[0]) {
          const file = files[0]
          await this.uploadFileToOss(file, callback, meta)
        }
      }
      input.click()
    },

    // 上传文件到 OSS（图片：与 CommonUpload 一致的分片上传；音视频：简单 put）
    async uploadFileToOss(file, callback, meta) {
      this.editorLoading = true
      try {
        const isImagePicker = meta && meta.filetype === 'image'
        const isImageFile = file.type && (file.type === 'image/jpeg' || file.type === 'image/png')
        if (isImagePicker || isImageFile) {
          const v = validateJpgPngMax2M(file)
          if (!v.ok) {
            this.$message.error(v.message)
            return
          }
          const base = `${Date.now()}_${(file.name || 'img').replace(/\W+/g, '_')}`
          const { url } = await uploadImageMultipart(ossApi, file, 'news/content/', base)
          callback(url, { title: file.name })
          return
        }

        const res = await ossApi.getStsAkV2('news/content/')
        const region = res.endpoint
        const fKey = region.indexOf('//') + 2
        const lKey = region.indexOf('.')
        const client = new OSS({
          region: region.substring(fKey, lKey),
          accessKeyId: res.accessKeyId,
          accessKeySecret: res.accessKeySecret,
          stsToken: res.securityToken,
          bucket: res.bucketName,
        })
        const fileName = `${Date.now()}_${file.name || 'media'}`
        const result = await client.put(`${res.url}${fileName}`, file)
        callback(result.url, { title: file.name })
      } catch (error) {
        console.error('文件上传失败:', error)
        this.$message.error('文件上传失败')
      } finally {
        this.editorLoading = false
      }
    },

    // 图片上传处理器（粘贴 / 拖拽）
    async handleImageUpload(blobInfo, success, failure) {
      const raw = blobInfo.blob()
      const name = blobInfo.filename() || `paste_${Date.now()}.png`
      const mime = raw.type || 'image/png'
      const file = raw instanceof File ? raw : new File([raw], name, { type: mime })

      const v = validateJpgPngMax2M(file)
      if (!v.ok) {
        this.$message.error(v.message)
        failure(v.message)
        return
      }

      this.editorLoading = true
      try {
        const base = `${Date.now()}_${name.replace(/\W+/g, '_')}`
        const { url } = await uploadImageMultipart(ossApi, file, 'news/content/', base)
        success(url)
      } catch (error) {
        console.error('图片上传失败:', error)
        this.$message.error('图片上传失败')
        failure('图片上传失败')
      } finally {
        this.editorLoading = false
      }
    },

    // 提交表单
    handleSubmit() {
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) {
          return
        }

        this.submitting = true
        try {
          if (this.isEdit) {
            // 更新资讯
            const updateForm = {
              title: this.form.title,
              categoryId: this.form.categoryId,
              content: this.form.content,
              summary: this.form.summary || undefined,
              coverUri: this.form.coverUri
            }
            
            await newsManageApi.updateNews(this.newsId, updateForm)
            this.$message.success('更新成功')
          } else {
            // 创建资讯
            const createForm = {
              title: this.form.title,
              categoryId: this.form.categoryId,
              content: this.form.content,
              summary: this.form.summary || undefined,
              coverUri: this.form.coverUri || undefined,
            }
            
            await newsManageApi.createNews(createForm)
            this.$message.success('创建成功')
          }
          
          // 返回列表页
          this.$router.push({ name: 'NewsManagement' })
        } catch (error) {
          console.error('操作失败:', error)
          this.$message.error(error.message || '操作失败')
        } finally {
          this.submitting = false
        }
      })
    },

    // 返回
    handleBack() {
      this.$router.back()
    }
  }
}
</script>

<style lang="less" scoped>
.news-editor-container {
  padding: 0 24px 24px;

  .upload-tip {
    margin-top: 8px;
    font-size: 12px;
    color: #999;
  }

  .editor-item {
    /deep/ .ant-form-item-control {
      line-height: normal;
    }
  }

  /deep/ .tox-tinymce {
    border-radius: 4px;
  }

  /deep/ .ant-page-header {
    padding: 16px 0;
    background: transparent;
  }
}
</style>

